// Resolving a captured duplicate — the pure parts.
//
// ── Why this is its own file ──────────────────────────────────────────────
// Parser v51 stores a re-sent invoice as a `duplicate` row instead of throwing
// the email away. Deciding what it MEANS is the part with money in it, and it
// is not one decision:
//
//   FULL        the original was never a real sale (the $0-demo case). The
//               captured amount is the sale; restore the invoice number.
//   ADJUSTMENT  the original WAS real and is already commissioned. The invoice
//               was then increased and QuickBooks re-sent the whole thing.
//               Only the delta is new revenue.
//
// Invoice 25012 on 2026-08-03 is the second case: $4,530 commissioned in July,
// re-sent at $6,030. Promoting the full $6,030 pays AGL a second time on the
// $4,530 they were already paid for. The difference — $1,500 — is the sale, and
// it belongs to the month the increase landed in, not July's.
//
// These live outside App.js so the arithmetic can be tested without rendering
// the app. App.js keeps the I/O and the dialog; nothing here touches Supabase.

/**
 * The real invoice number behind a stored one.
 *
 * The parser suffixes a captured duplicate (-DUP, -DUP2 …) because entries has
 * UNIQUE (invoice_normalized, rep) and invoice_normalized is generated from
 * invoice. Adjustments get -ADD for the same reason. Both are storage
 * mechanics; the number a human is looking for is the base.
 */
export function realInvoiceOf(invoice) {
  return String(invoice || '').replace(/-(DUP|ADD)\d*$/i, '')
}

/**
 * Nth adjustment invoice for a base number: -ADD, -ADD2, -ADD3 …
 *
 * Escalates for the same reason the parser's -DUP does — a second increase to
 * the same invoice is an ordinary thing, and the unique index does not care
 * that the first one was also an adjustment.
 */
export function adjustmentInvoice(base, n) {
  return n <= 1 ? `${base}-ADD` : `${base}-ADD${n}`
}

/** Captured minus already-recorded, or null when there is nothing to compare. */
export function computeDifference(captured, existingAmount) {
  if (existingAmount == null) return null
  const c = Number(captured), e = Number(existingAmount)
  if (!Number.isFinite(c) || !Number.isFinite(e)) return null
  return Number((c - e).toFixed(2))
}

/**
 * What the amount field should open on.
 *
 * A positive difference wins, because that is the case that silently overpays —
 * a dialog defaulting to the full amount there is a dialog that quietly hands
 * someone commission twice. Everything else falls back to the captured amount:
 * a zero or negative difference means the re-send was not an increase, so there
 * is no delta to offer and the operator is choosing, not correcting.
 */
export function defaultPromoteAmount(captured, difference) {
  return difference != null && difference > 0 ? difference : Number(captured)
}

/**
 * A user-typed amount as a number, or null if it isn't one.
 *
 * Number('') is 0 — finite, and therefore accepted by a naive isFinite guard.
 * The dialog's amount is an input value, so a cleared field would sail through
 * validation and record a $0 sale with $0 commission, silently, looking exactly
 * like a deliberate entry. Blank and whitespace are rejected here so both the
 * preview and the write agree on what "no amount" means.
 */
export function parseAmountInput(v) {
  if (v == null) return null
  if (typeof v === 'string' && v.trim() === '') return null
  const n = Number(v)
  return Number.isFinite(n) ? n : null
}

/**
 * Commission for an amount, via the standard formula. Deliberately takes a
 * rate rather than a rep/tier lookup so this file holds no rate table — there
 * is exactly one of those, in App.js, and a second copy would drift.
 */
export function commissionFor(amount, rate) {
  const a = parseAmountInput(amount)
  if (a == null) return null
  return a * 0.9 * (Number(rate) || 0)
}

/** The note recorded on an adjustment, so the row explains itself later. */
export function adjustmentNote(base, existingAmount, capturedAmount, fmt) {
  const money = fmt || (n => `$${Number(n).toFixed(2)}`)
  return existingAmount != null
    ? `Adjustment to #${base} — original ${money(existingAmount)}, updated total ${money(capturedAmount)}`
    : `Adjustment to #${base} — captured total ${money(capturedAmount)}`
}
